/**
 * Filter Preset Manager Component
 * Allows saving and loading filter configurations
 */
import { useState, useEffect, useCallback } from 'react';
import { Save, FolderOpen, Star, Trash2, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FilterPresetManager({ 
  page, // 'job_cards', 'invoices', 'reports', 'customers'
  currentFilters, 
  onLoadPreset,
  className = ''
}) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  // Fetch presets for this page
  const fetchPresets = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/filter-presets?page=${page}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
        
        // Auto-load default preset if available
        const defaultPreset = data.presets?.find(p => p.is_default);
        if (defaultPreset && onLoadPreset) {
          onLoadPreset(defaultPreset.filters);
        }
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    }
  }, [page, onLoadPreset]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  // Save current filters as a preset
  const savePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/filter-presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: presetName.trim(),
          page: page,
          filters: currentFilters,
          is_default: isDefault
        })
      });

      if (response.ok) {
        toast.success('Filter preset saved');
        setShowSaveDialog(false);
        setPresetName('');
        setIsDefault(false);
        fetchPresets();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save preset');
      }
    } catch (error) {
      toast.error('Failed to save preset');
    } finally {
      setLoading(false);
    }
  };

  // Load a preset
  const loadPreset = (preset) => {
    if (onLoadPreset) {
      onLoadPreset(preset.filters);
      toast.success(`Loaded preset: ${preset.name}`);
    }
  };

  // Set preset as default
  const setAsDefault = async (presetId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/filter-presets/${presetId}/set-default`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Default preset updated');
        fetchPresets();
      }
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  // Delete a preset
  const deletePreset = async (presetId, presetName) => {
    if (!confirm(`Delete preset "${presetName}"?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/filter-presets/${presetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Preset deleted');
        fetchPresets();
      }
    } catch (error) {
      toast.error('Failed to delete preset');
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Load Preset Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            data-testid="load-preset-button"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Load Preset
            {presets.length > 0 && (
              <span className="ml-1 text-xs text-slate-500">({presets.length})</span>
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="start" className="w-64">
          {presets.length === 0 ? (
            <div className="py-4 px-3 text-center text-slate-500 text-sm">
              No saved presets
            </div>
          ) : (
            presets.map((preset) => (
              <DropdownMenuItem
                key={preset.preset_id}
                className="flex items-center justify-between cursor-pointer"
                onSelect={() => loadPreset(preset)}
                data-testid={`preset-item-${preset.preset_id}`}
              >
                <div className="flex items-center gap-2">
                  {preset.is_default && (
                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                  )}
                  <span>{preset.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAsDefault(preset.preset_id);
                    }}
                    title="Set as default"
                  >
                    <Star className={`h-3 w-3 ${preset.is_default ? 'text-amber-500 fill-amber-500' : 'text-slate-400'}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-red-500 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePreset(preset.preset_id, preset.name);
                    }}
                    title="Delete preset"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save Preset Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            data-testid="save-preset-button"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Preset
          </Button>
        </DialogTrigger>
        
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>
              Save your current filter settings to quickly apply them later.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., Active Jobs This Week"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                data-testid="preset-name-input"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-default"
                checked={isDefault}
                onCheckedChange={setIsDefault}
              />
              <Label htmlFor="is-default" className="text-sm font-normal">
                Set as default (auto-load when opening this page)
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSaveDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={savePreset} 
              disabled={loading || !presetName.trim()}
              data-testid="confirm-save-preset"
            >
              {loading ? 'Saving...' : 'Save Preset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
